import { useMemo } from "react";
import { useCanRender, useHowManyItems } from "hooks/useRender";
import useTranslation from "next-translate/useTranslation";
import { useBusinessProfile } from "./useFetch";
import { generateSectionLabel } from "utils/utils";

export const useMakeLabel = ({
  field,
  limit = 13,
  transKey,
}: {
  field: any;
  limit?: number;
  transKey: string;
}) => {
  const { data: businessProfile } = useBusinessProfile();
  const widgetSetting = businessProfile?.data.widget;
  const { t } = useTranslation("common");
  const widgetTranslations = widgetSetting?.settings.language.translations;
  const sectionLabel = widgetTranslations?.["en"]?.[field] || transKey;
  return generateSectionLabel(t(sectionLabel), limit);
};

const useTabs = () => {
  const { t } = useTranslation("common");
  const isProviderRenderable = useCanRender("provider");
  const isReviewRenderable = useCanRender("review");

  const hasProvidersItems = useHowManyItems("providers") > 0;
  const hasReviewsItems = useHowManyItems("reviews") > 0;
  const hasServicesItems = useHowManyItems("services") > 0;

  const providerLabel = useMakeLabel({
    field: "provider",
    transKey: "navigation.provider",
  });
  const serviceLabel = useMakeLabel({
    field: "service",
    transKey: "navigation.service",
  });
  const reviewLabel = useMakeLabel({
    field: "review",
    transKey: "navigation.review",
  });

  const tabs = useMemo(() => {
    return [
      { id: 0, label: t(`navigation.about`), path: "about", visible: true },
      {
        id: 1,
        label: providerLabel,
        path: "providers",
        visible: hasProvidersItems && isProviderRenderable,
      },

      {
        id: 2,
        label: serviceLabel,
        path: "services",
        visible: hasServicesItems,
      },
      {
        id: 3,
        label: reviewLabel,
        path: "reviews",
        visible: hasReviewsItems && isReviewRenderable,
      },
    ];
  }, [
    hasProvidersItems,
    hasReviewsItems,
    hasServicesItems,
    isProviderRenderable,
    isReviewRenderable,
    providerLabel,
    reviewLabel,
    serviceLabel,
    t,
  ]);

  return tabs;
};

export default useTabs;
